import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * Admin AI Dashboard — cross-deal AI usage & revenue.
 * Shows total revenue, provider cost, gross profit, gross margin, request
 * count, deposits, wallet counts by state, and a filterable usage ledger.
 */
export default function AiTab() {
  const [ov, setOv] = useState(null);
  const [usage, setUsage] = useState([]);
  const [dealId, setDealId] = useState("");
  const [userId, setUserId] = useState("");
  const [model, setModel] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    api.get("/admin/ai/overview").then((r) => setOv(r.data)).catch(() => {});
    loadUsage();
  };
  const loadUsage = () => {
    setLoading(true);
    const params = { limit: 200 };
    if (dealId) params.deal_id = dealId;
    if (userId) params.user_id = userId;
    if (model)  params.model   = model;
    api.get("/admin/ai/usage", { params })
      .then((r) => setUsage(r.data.items || []))
      .catch(() => setUsage([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  useEffect(loadUsage, [dealId, userId, model]);

  return (
    <div className="space-y-6" data-testid="admin-ai-tab">
      {ov && (
        <>
          <section>
            <div className="font-overline text-accent mb-3">Revenue & Margin</div>
            <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-border">
              <Kpi label="Revenue" value={fmtMoney(ov.revenue_usd)} tone="ok" testid="ai-kpi-revenue" />
              <Kpi label="Provider Cost" value={fmtMoney(ov.provider_cost)} testid="ai-kpi-cost" />
              <Kpi label="Gross Profit" value={fmtMoney(ov.gross_profit)} tone="ok" testid="ai-kpi-profit" />
              <Kpi label="Gross Margin" value={`${((ov.gross_margin || 0) * 100).toFixed(1)}%`} tone={ov.gross_margin >= 0.45 ? "ok" : "warn"} testid="ai-kpi-margin" />
              <Kpi label="Requests" value={ov.requests_count} testid="ai-kpi-requests" />
              <Kpi label="Deposits Total" value={fmtMoney(ov.deposits_total)} testid="ai-kpi-deposits" />
              <Kpi label="Deposits Count" value={ov.deposits_count} testid="ai-kpi-deposits-count" />
              <Kpi label="Failed Requests" value={ov.failed_requests} tone={ov.failed_requests > 0 ? "warn" : "default"} testid="ai-kpi-failed" />
            </div>
          </section>

          <section>
            <div className="font-overline text-accent mb-3">Wallets</div>
            <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-border">
              <Kpi label="Active Wallets" value={ov.active_wallets} testid="ai-kpi-wallets-active" />
              <Kpi label="Low Balance" value={ov.low_balance_wallets} tone={ov.low_balance_wallets > 0 ? "warn" : "default"} testid="ai-kpi-wallets-low" />
              <Kpi label="Paused" value={ov.paused_wallets} testid="ai-kpi-wallets-paused" />
              <Kpi label="Auto-Reload Users" value={ov.auto_reload_users} testid="ai-kpi-wallets-auto" />
            </div>
          </section>

          {ov.usage_by_model?.length > 0 && (
            <section>
              <div className="font-overline text-accent mb-3">Usage by Model</div>
              <div className="border border-border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left">
                    <tr><Th>Model</Th><Th>Requests</Th><Th>Provider Cost</Th><Th>Revenue</Th><Th>Gross Profit</Th></tr>
                  </thead>
                  <tbody>
                    {ov.usage_by_model.map((m) => (
                      <tr key={m._id || m.id || "unknown"} className="border-t border-border" data-testid={`ai-model-${m._id}`}>
                        <Td className="font-mono text-[11px]">{m._id || "—"}</Td>
                        <Td className="font-mono">{m.count}</Td>
                        <Td className="font-mono-data">{fmtMoney(m.provider_cost || 0)}</Td>
                        <Td className="font-mono-data">{fmtMoney(m.user_charge || 0)}</Td>
                        <Td className="font-mono-data text-emerald-600">{fmtMoney(m.gross_profit || 0)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}

      <section>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="font-overline text-accent">Usage Ledger</div>
          <div className="flex gap-2 flex-wrap">
            <Input placeholder="Deal ID" value={dealId} onChange={(e) => setDealId(e.target.value)} className="rounded-none h-9 w-40" data-testid="ai-filter-deal" />
            <Input placeholder="User ID" value={userId} onChange={(e) => setUserId(e.target.value)} className="rounded-none h-9 w-40" data-testid="ai-filter-user" />
            <Input placeholder="Model" value={model} onChange={(e) => setModel(e.target.value)} className="rounded-none h-9 w-32" data-testid="ai-filter-model" />
            <Button variant="outline" className="rounded-none uppercase text-xs h-9" onClick={load} data-testid="ai-refresh">Refresh</Button>
          </div>
        </div>
        <div className="border border-border overflow-x-auto" data-testid="ai-usage-table">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-muted/50 text-left">
              <tr>
                <Th>When</Th><Th>Deal</Th><Th>User</Th><Th>Role</Th><Th>Model</Th>
                <Th>Tokens (In / Out)</Th><Th>Provider Cost</Th><Th>User Charge</Th><Th>Profit</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">Loading…</td></tr>}
              {!loading && usage.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">No usage recorded.</td></tr>}
              {usage.map((u) => (
                <tr key={u.usage_id} className="border-t border-border" data-testid={`ai-usage-${u.usage_id}`}>
                  <Td className="font-mono text-[10px] whitespace-nowrap">{u.at?.slice(0, 16).replace("T", " ")}</Td>
                  <Td className="font-mono text-[10px]"><Link className="text-primary hover:underline" to={`/deals/${u.deal_id}`}>{u.deal_id?.slice(-10)}</Link></Td>
                  <Td className="font-mono text-[10px]">{u.user_id?.slice(-8)}</Td>
                  <Td className="text-[10px] font-mono uppercase">{u.role}</Td>
                  <Td className="text-[10px] font-mono">{u.model}</Td>
                  <Td className="text-[10px] font-mono">{u.in_tokens} / {u.out_tokens}</Td>
                  <Td className="font-mono-data text-[11px]">{fmtMoney(u.provider_cost || 0)}</Td>
                  <Td className="font-mono-data text-[11px] text-primary">{fmtMoney(u.user_charge || 0)}</Td>
                  <Td className="font-mono-data text-[11px] text-emerald-600">{fmtMoney(u.gross_profit || 0)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

const Th = ({ children }) => <th className="px-3 py-2 font-overline text-[10px] text-muted-foreground whitespace-nowrap">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2 ${className}`}>{children}</td>;
function Kpi({ label, value, tone, testid }) {
  const t = tone === "ok" ? "text-emerald-600" : tone === "warn" ? "text-amber-600" : "";
  return (
    <div className="p-5 border-r border-b border-border" data-testid={testid}>
      <div className="font-overline text-[10px] text-muted-foreground">{label}</div>
      <div className={`font-mono-data text-2xl mt-1 font-light ${t}`}>{value}</div>
    </div>
  );
}
